import * as React from "react"
import { cn } from "../../lib/utils"
import { Tooltip } from "./tooltip"
import { Info } from "lucide-react"

// SVG shape components for visual examples
const PerfectCircle = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 40 40" className={cn("w-10 h-10", className)}>
    <circle cx="20" cy="20" r="16" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2" />
  </svg>
)

const IndentedShape = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 40 40" className={cn("w-10 h-10", className)}>
    <path d="M20 4 L36 12 L32 20 L36 28 L20 36 L4 28 L8 20 L4 12 Z" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2" />
  </svg>
)

const BrokenShape = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 40 40" className={cn("w-10 h-10", className)}>
    <path d="M20 4 C30 4 36 14 36 20 C36 26 30 36 20 36" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M20 4 C10 4 4 14 4 20" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" />
    <circle cx="8" cy="28" r="3" fill="currentColor" opacity="0.5" />
  </svg>
)

// Vertical ellipse (taller than wide)
const VerticalEllipse = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 40 50" className={cn("w-8 h-10", className)}>
    <ellipse cx="20" cy="25" rx="12" ry="22" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2" />
  </svg>
)

// Horizontal ellipse (wider than tall)
const HorizontalEllipse = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 50 40" className={cn("w-10 h-8", className)}>
    <ellipse cx="25" cy="20" rx="22" ry="12" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2" />
  </svg>
)

// Metric explanation content - SCIENCE FIRST approach
const METRIC_EXPLANATIONS: Record<string, React.ReactNode> = {
  roundness: (
    <div className="space-y-2.5">
      <div className="font-semibold text-foreground">Roundness</div>
      <p className="text-muted-foreground text-xs leading-relaxed">
        How close to a <span className="font-medium text-foreground">perfect circle</span> is the microsphere?
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col items-center p-2 bg-green-500/10 rounded border border-green-500/30">
          <PerfectCircle className="text-green-600 mb-1" />
          <span className="text-[10px] font-semibold text-green-700">1.0 = Perfect sphere</span>
          <span className="text-[9px] text-green-600">Ideal</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-orange-500/10 rounded border border-orange-500/30">
          <IndentedShape className="text-orange-600 mb-1" />
          <span className="text-[10px] font-semibold text-orange-700">Low = Irregular</span>
          <span className="text-[9px] text-orange-600">Not circular</span>
        </div>
      </div>
    </div>
  ),
  convexity: (
    <div className="space-y-2.5">
      <div className="font-semibold text-foreground">Convexity</div>
      <p className="text-muted-foreground text-xs leading-relaxed">
        How <span className="font-medium text-foreground">smooth</span> are the edges? Measures bumps and dents on the surface.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col items-center p-2 bg-green-500/10 rounded border border-green-500/30">
          <PerfectCircle className="text-green-600 mb-1" />
          <span className="text-[10px] font-semibold text-green-700">1.0 = Smooth edges</span>
          <span className="text-[9px] text-green-600">No bumps or dents</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-orange-500/10 rounded border border-orange-500/30">
          <IndentedShape className="text-orange-600 mb-1" />
          <span className="text-[10px] font-semibold text-orange-700">Low = Rough edges</span>
          <span className="text-[9px] text-orange-600">Has bumps or dents</span>
        </div>
      </div>
    </div>
  ),
  "aspect ratio": (
    <div className="space-y-2.5">
      <div className="font-semibold text-foreground">Aspect Ratio</div>
      <p className="text-muted-foreground text-xs leading-relaxed">
        Ratio of <span className="font-medium text-foreground">width to height</span>. Shows if the microsphere is stretched horizontally or vertically.
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        <div className="flex flex-col items-center p-2 bg-orange-500/10 rounded border border-orange-500/30">
          <VerticalEllipse className="text-orange-600 mb-1" />
          <span className="text-[10px] font-semibold text-orange-700">{"<1.0"}</span>
          <span className="text-[9px] text-orange-600 text-center">Taller than wide</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-green-500/10 rounded border border-green-500/30">
          <PerfectCircle className="text-green-600 mb-1" />
          <span className="text-[10px] font-semibold text-green-700">1.0</span>
          <span className="text-[9px] text-green-600 text-center">Equal (Ideal)</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-orange-500/10 rounded border border-orange-500/30">
          <HorizontalEllipse className="text-orange-600 mb-1" />
          <span className="text-[10px] font-semibold text-orange-700">{">1.0"}</span>
          <span className="text-[9px] text-orange-600 text-center">Wider than tall</span>
        </div>
      </div>
    </div>
  ),
  solidity: (
    <div className="space-y-2.5">
      <div className="font-semibold text-foreground">Solidity</div>
      <p className="text-muted-foreground text-xs leading-relaxed">
        How <span className="font-medium text-foreground">complete</span> is the microsphere? Detects holes, breaks, or missing pieces.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col items-center p-2 bg-green-500/10 rounded border border-green-500/30">
          <PerfectCircle className="text-green-600 mb-1" />
          <span className="text-[10px] font-semibold text-green-700">1.0 = Complete</span>
          <span className="text-[9px] text-green-600">Fully intact</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-orange-500/10 rounded border border-orange-500/30">
          <BrokenShape className="text-orange-600 mb-1" />
          <span className="text-[10px] font-semibold text-orange-700">Low = Incomplete</span>
          <span className="text-[9px] text-orange-600">Has holes or breaks</span>
        </div>
      </div>
    </div>
  ),
}

interface ShapeMetricBarProps {
  label: string
  value: number
  maxValue?: number  // For aspect ratio which can be > 1
  color?: string
  className?: string
}

const ShapeMetricBar = React.forwardRef<HTMLDivElement, ShapeMetricBarProps>(
  ({ label, value, maxValue = 1, color = "#3b82f6", className }, ref) => {
    const isAspectRatio = label.toLowerCase() === "aspect ratio"

    // Calculate percentage (clamped to 0-1) for ring visualization
    const normalizedValue = Math.min(Math.max(value / maxValue, 0), 1)

    // SVG ring properties
    const size = 48
    const strokeWidth = 5
    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const dashLength = circumference * normalizedValue

    // Get tooltip content for this metric
    const tooltipContent = METRIC_EXPLANATIONS[label.toLowerCase()]

    if (isAspectRatio) {
      // Same layout as others but without the ring - just empty space for alignment
      return (
        <div ref={ref} className={cn("flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-3 py-2", className)}>
          {/* Empty space to match ring size */}
          <div className="w-12 h-12 shrink-0" />

          {/* Label and Value - same as other metrics */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground uppercase tracking-wide truncate">{label}</span>
              {tooltipContent && (
                <Tooltip content={tooltipContent}>
                  <Info className="w-3 h-3 text-muted-foreground/60 hover:text-muted-foreground cursor-help transition-colors" />
                </Tooltip>
              )}
            </div>
            <div className="text-sm font-bold font-mono truncate">{value.toFixed(3)}</div>
          </div>
        </div>
      )
    }

    return (
      <div ref={ref} className={cn("flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-3 py-2", className)}>
        {/* Radial Ring for other metrics */}
        <svg width={size} height={size} className="shrink-0 -rotate-90">
          {/* Background track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            className="stroke-muted/60"
            fill="none"
          />
          {/* Colored progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            style={{
              stroke: color,
              strokeDasharray: `${dashLength} ${circumference - dashLength}`,
              transition: 'stroke-dasharray 0.5s ease-out'
            }}
          />
        </svg>

        {/* Label and Value */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide truncate">{label}</span>
            {tooltipContent && (
              <Tooltip content={tooltipContent}>
                <Info className="w-3 h-3 text-muted-foreground/60 hover:text-muted-foreground cursor-help transition-colors" />
              </Tooltip>
            )}
          </div>
          <div className="text-sm font-bold font-mono truncate">{value.toFixed(3)}</div>
        </div>
      </div>
    )
  }
)

ShapeMetricBar.displayName = "ShapeMetricBar"

export { ShapeMetricBar }
